import {
  OnboardingTarget,
  onboardingTarget,
} from "../onboarding/onboardingTargets";
import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import {
  selectCurrentEnvironmentId,
  selectEnvironmentInteractionPoints,
  selectEnvironmentLayers,
  selectCurrentEnvironmentThumbnail,
} from "../../app/slices/EnvironmentSlice";
import {
  selectIsPlaying,
  selectSelectedElement,
  selectSelectedElementType,
  selectSelectedEnvironmentLayerIds,
  selectSelectedTake3D,
  loadSelectedEnvironmentLayerIds,
  setSelectedEnvironmentLayerIds,
  setImageBounds,
  setViewportBounds,
} from "../../app/slices/TrainingSlice";
import Interaction, { InteractionType } from "../../models/interactions/Interaction";
import ViewportMap3D from "../leaflet/ViewportMap3D";
import { calculateContainedImageBounds } from "../training_preview/ImageBounds";
import TrainingPreviewOverlay from "../training_preview/TrainingPreviewOverlay";
import CameraWidget from "./CameraWidget";
import TakeCameraPickingBar from "./TakeCameraPickingBar";
import "./Viewport3D.css";
import ViewportTools from "../viewport_tools/ViewportTools";
import PropLibrary from "../prop_library/PropLibrary";
import useViewport3D from "./Viewport3D.hook";
import {
  closeSequenceMapPeek,
  closeSequencePositionPicker,
  openSequenceMapPeek,
  selectIsSequenceMapRemembered,
  setSequenceMapRemembered,
  requestViewportMapFocus,
  selectIsSequenceMapPeekOpen,
  SelectedTab,
  selectSelectedTab,
  selectSequencePositionPicker,
  selectShowEnvironmentProps,
  setShowEnvironmentProps,
} from "../../app/slices/GlobalSlice";
import QuickActorModal from "./QuickActorModal";
import ViewportSplitHandle, { useViewportSplitRatio } from "./ViewportSplitHandle";
import useSequenceStateOnMap from "./useSequenceStateOnMap";
import { isSplitDragging } from "./splitDragState";
import ViewportLayerFilterControl from "./ViewportLayerFilterControl";
import ViewportActorStateWindow from "./ViewportActorStateWindow";
import ViewportTransitionWindow from "./ViewportTransitionWindow";
import ActorPresetEditor from "../actor_presets/ActorPresetEditor";
import PropPresetEditor from "../prop_presets/PropPresetEditor";
import ViewportEnvironmentPropsControl from "./ViewportEnvironmentPropsControl";

export default function Viewport3D() {
  const url = useSelector(selectCurrentEnvironmentThumbnail);

  const dispatch = useDispatch();
  const imgRef = useRef<HTMLImageElement>(null);
  const isPlaying = useSelector(selectIsPlaying);
  const selectedElement = useSelector(selectSelectedElement);
  const selectedElementType = useSelector(selectSelectedElementType);
  const currentEnvironmentId = useSelector(selectCurrentEnvironmentId);
  const currentTab = useSelector(selectSelectedTab);
  const layers = useSelector(selectEnvironmentLayers);
  const interactionPoints = useSelector(selectEnvironmentInteractionPoints);
  const selectedLayerIds = useSelector(selectSelectedEnvironmentLayerIds);
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);
  const isMapPeekOpen = useSelector(selectIsSequenceMapPeekOpen);
  // Picking a position swaps the sequence canvas for the map; looking at one
  // splits the viewport: the canvas on top, the map below.
  // The take location picker comes from the Take column, not a sequence: it still swaps.
  const isSwap =
    sequencePositionPicker.isOpen &&
    sequencePositionPicker.positionType === "takeLocation";
  const isPickerSplit = sequencePositionPicker.isOpen && !isSwap;
  const isSplit = isPickerSplit || isMapPeekOpen;
  // Cancelling a pick closes its map too; picking a position leaves the map open.
  const cancelPicking = useCallback(() => {
    dispatch(closeSequencePositionPicker());
    dispatch(closeSequenceMapPeek());
  }, [dispatch]);
  // The user closing the map (×, Escape, dragging it shut): the next sequence opens without it.
  const closeSplit = useCallback(() => {
    cancelPicking();
    dispatch(setSequenceMapRemembered(false));
  }, [cancelPicking, dispatch]);
  // Opened by dragging: fly to the take location once the drag ends (smooth drag).
  const openedByDragRef = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const sequenceStateOnMap = useSequenceStateOnMap();
  const [splitRatio, setSplitRatio] = useViewportSplitRatio();
  // Dragging the bottom grip up past this opens the map; dropping the bar below it closes the map.
  const SPLIT_EDGE = 0.9;
  const showEnvironmentProps = useSelector(selectShowEnvironmentProps);
  const [openViewportDropdown, setOpenViewportDropdown] = useState<
    "layers" | "environmentProps" | null
  >(null);

  const {
    closePropLibrary,
    isPropLibraryOpen,
    isQuickActorModalOpen,
    closeQuickActorModal,
  } = useViewport3D();

  const selectedTake = useSelector(selectSelectedTake3D);
  // Where the selected take plays: a map opened below the sequence starts there.
  const takeLocationPoint = (interactionPoints ?? []).find(
    (point) => point.id === selectedTake?.interactionPointId,
  )?.position;

  const resizeSplit = (ratio: number) => {
    if (!isSplit) {
      if (ratio >= SPLIT_EDGE) return;
      const dragging = isSplitDragging();
      openedByDragRef.current = dragging;
      dispatch(openSequenceMapPeek(dragging ? undefined : takeLocationPoint));
    }
    setSplitRatio(ratio);
  };

  const endSplitDrag = (ratio: number) => {
    if (isSplit && ratio > SPLIT_EDGE) closeSplit();
    else if (openedByDragRef.current && takeLocationPoint)
      dispatch(requestViewportMapFocus(takeLocationPoint));
    openedByDragRef.current = false;
  };


  // A position picker opens on the take's location too.
  useEffect(() => {
    if (isPickerSplit && takeLocationPoint)
      dispatch(requestViewportMapFocus(takeLocationPoint));
    // Only when a picker opens, not when the take location data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPickerSplit, sequencePositionPicker.nodeId]);

  const isCameraSelected = useMemo(() => {
    return selectedElement?.id === selectedTake?.mainCamera.id;
  }, [selectedElement, selectedTake]);
  const isSequenceContext = useMemo(() => {
    const selectedInteraction =
      selectedElementType === "interaction"
        ? (selectedElement as Interaction | undefined)
        : undefined;

    return (
      selectedElementType === "takeSequence" ||
      selectedInteraction?.type === InteractionType.Sequence
    );
  }, [selectedElement, selectedElementType]);
  const isTransitionSelected = selectedElementType === "transition";
  const presetEditor =
    currentTab === SelectedTab.ActorPresets ? (
      <ActorPresetEditor />
    ) : currentTab === SelectedTab.PropPresets ? (
      <PropPresetEditor />
    ) : null;

  const availableLayers = useMemo(() => {
    if (layers.length > 0) return layers;

    const uniqueLayers = Array.from(
      new Set(
        interactionPoints
          .map((position) => position.layer)
          .filter((layer): layer is string => !!layer)
      )
    );

    return uniqueLayers.map((id) => ({ id, name: id }));
  }, [layers, interactionPoints]);

  const availableLayerIdsKey = useMemo(() => {
    return availableLayers.map((layer) => layer.id).join("|");
  }, [availableLayers]);

  useEffect(() => {
    if (availableLayers.length === 0) {
      if (selectedLayerIds.length > 0) {
        dispatch(loadSelectedEnvironmentLayerIds([]));
      }
      return;
    }

    const defaultLayer = availableLayers.find((layer) => layer.id === "default")
      ?.id;
    const firstLayerId = availableLayers[0].id;
    const validSelection = selectedLayerIds.filter((id) =>
      availableLayers.some((layer) => layer.id === id)
    );
    if (validSelection.length > 0) {
      const unchanged =
        validSelection.length === selectedLayerIds.length &&
        validSelection.every((id, index) => id === selectedLayerIds[index]);
      if (!unchanged) {
        dispatch(loadSelectedEnvironmentLayerIds(validSelection));
      }
      return;
    }

    const fallback = [defaultLayer ?? firstLayerId];
    const fallbackUnchanged =
      fallback.length === selectedLayerIds.length &&
      fallback.every((id, index) => id === selectedLayerIds[index]);
    if (!fallbackUnchanged) {
      dispatch(loadSelectedEnvironmentLayerIds(fallback));
    }
  }, [
    currentEnvironmentId,
    availableLayerIdsKey,
    availableLayers,
    dispatch,
    selectedLayerIds,
  ]);

  const onLoad = useCallback(async () => {
    // get actual image size
    if (imgRef.current) {
      let img = new Image();
      img.src = url === undefined ? "" : url!;

      img.onload = async () => {
        var b = imgRef.current?.getBoundingClientRect();
        if (b) {
          const imgBounds = calculateContainedImageBounds(
            img.width,
            img.height,
            b.width,
            b.height
          );

          dispatch(
            setViewportBounds({
              x: b.x,
              y: b.y,
              width: b.width,
              height: b.height,
            })
          );
          dispatch(setImageBounds(imgBounds));
        }
      };
    }
  }, [dispatch, setImageBounds, setViewportBounds, imgRef, url]);

  const onWindowResize = useCallback(() => {
    onLoad();
  }, [onLoad]);

  // resize observer
  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === imgRef.current) {
          onLoad();
        }
      }
    });

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [onLoad]);

  useEffect(() => {
    // window resize
    window.addEventListener("resize", onWindowResize);

    return () => window.removeEventListener("resize", onWindowResize);
  });

  // resize after stop playing -> the transition messes up the calculations
  useEffect(() => {
    if (!isPlaying) {
      const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));
      const waitResize = async () => {
        await delay(150);
        onWindowResize();
      };

      waitResize();
    }
  }, [isPlaying, onWindowResize]);

  useEffect(() => {
    if (!sequencePositionPicker.isOpen) return;
    const isValidContext =
      sequencePositionPicker.positionType === "takeLocation"
        ? selectedElementType === "take"
        : isSequenceContext;
    if (currentTab !== SelectedTab.Timeline || !isValidContext) {
      cancelPicking();
    }
  }, [
    cancelPicking,
    currentTab,
    isSequenceContext,
    selectedElementType,
    sequencePositionPicker.isOpen,
    sequencePositionPicker.positionType,
  ]);

  // Escape cancels picking (or closes the map), unless something else handled it first.
  const isAnyPickerOpen = sequencePositionPicker.isOpen || isMapPeekOpen;
  useEffect(() => {
    if (!isAnyPickerOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest("input, textarea, select, [contenteditable='true']")
      )
        return;
      event.preventDefault();
      closeSplit();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeSplit, isAnyPickerOpen]);

  // A remembered map reopens with the next sequence (it closes when leaving one).
  const isMapRemembered = useSelector(selectIsSequenceMapRemembered);
  useEffect(() => {
    if (
      isMapRemembered &&
      currentTab === SelectedTab.Timeline &&
      isSequenceContext &&
      !isMapPeekOpen &&
      !sequencePositionPicker.isOpen
    )
      dispatch(openSequenceMapPeek(takeLocationPoint));
    // Only when the context changes, not when the take location data does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, isMapRemembered, isSequenceContext]);

  useEffect(() => {
    if (!isMapPeekOpen) return;
    if (currentTab !== SelectedTab.Timeline || !isSequenceContext) {
      dispatch(closeSequenceMapPeek());
    }
  }, [currentTab, dispatch, isMapPeekOpen, isSequenceContext]);

  return (
    <div
      ref={rootRef}
      className={classNames("viewport3d-root relative", {
        "viewport3d-root--split": isSplit,
      })}
      style={
        isSplit
          ? ({ "--viewport3d-split": `${splitRatio * 100}%` } as CSSProperties)
          : undefined
      }
      {...onboardingTarget(OnboardingTarget.Editor3DViewport)}
    >
      {selectedElement &&
        selectedElementType !== "actor" &&
        !isCameraSelected &&
        !isTransitionSelected &&
        !isSwap && <TrainingPreviewOverlay />}
      {isCameraSelected &&
        currentTab === SelectedTab.Timeline &&
        selectedTake?.startViewMode !== "propInspect" &&
        !sequencePositionPicker.isOpen && <TakeCameraPickingBar />}
      {/* One element in both states, so a drag that opens the map keeps resizing it. */}
      {(isSplit || (isSequenceContext && currentTab === SelectedTab.Timeline && !isSwap)) && (
        <ViewportSplitHandle
          containerRef={rootRef}
          isOpen={isSplit}
          ratio={splitRatio}
          onResize={resizeSplit}
          onDragEnd={endSplitDrag}
        />
      )}
      <div className="viewport3d-map" data-testid="viewport3d-map">
        <ViewportMap3D
          visibleLayerIds={selectedLayerIds}
          onBackgroundClick={
            sequencePositionPicker.isOpen ? cancelPicking : undefined
          }
        />
        {isSplit && (
          <div className="absolute left-[12px] right-[12px] top-[12px] z-[1000] flex items-start justify-end gap-[8px]">
            <div className="mr-auto flex flex-wrap items-center gap-[8px]">
              {isPickerSplit && sequencePositionPicker.title && (
                <span
                  className="rounded-full border border-solid border-creator-border bg-creator-surface px-[12px] py-[6px] text-[12px] font-medium text-creator-text shadow-md"
                  data-testid="viewport3d-picker-title"
                >
                  {sequencePositionPicker.title}
                </span>
              )}
              {sequenceStateOnMap && (
                <span
                  className="rounded-full border border-solid border-creator-border bg-creator-surface px-[12px] py-[6px] text-[12px] text-creator-text-soft shadow-md"
                  data-testid="viewport3d-state-chip"
                  title="Where actors and the camera are once this node has run, following the take and the nodes before it. If and Switch branches are skipped."
                >
                  State after{" "}
                  <span className="font-medium text-creator-text">
                    {sequenceStateOnMap.nodeTitle}
                  </span>
                </span>
              )}
            </div>
            <button
              type="button"
              aria-label={isPickerSplit ? "Cancel picking" : "Close map"}
              title={isPickerSplit ? "Cancel picking" : "Close map"}
              className="flex h-[32px] w-[32px] items-center justify-center rounded-full border border-solid border-creator-border bg-creator-surface text-creator-text shadow-md transition-colors hover:bg-creator-primary-background"
              onClick={closeSplit}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        )}
      </div>
      <CameraWidget />
      {presetEditor && (
        <aside
          aria-label="viewport-preset-editor"
          className="viewport3d-preset-editor"
        >
          <div className="viewport3d-preset-editor__panel">
            {presetEditor}
          </div>
        </aside>
      )}
      <ViewportActorStateWindow />
      <ViewportTransitionWindow />
      <ViewportTools>
        {currentTab === SelectedTab.Timeline && (
          <>
            {availableLayers.length > 0 && (
              <ViewportLayerFilterControl
                layers={availableLayers}
                selectedLayerIds={selectedLayerIds}
                onChange={(ids) => dispatch(setSelectedEnvironmentLayerIds(ids))}
                isOpen={openViewportDropdown === "layers"}
                onOpenChange={(isOpen) =>
                  setOpenViewportDropdown(isOpen ? "layers" : null)
                }
              />
            )}
            <ViewportEnvironmentPropsControl
              checked={showEnvironmentProps}
              onChange={(checked) => dispatch(setShowEnvironmentProps(checked))}
              isOpen={openViewportDropdown === "environmentProps"}
              onOpenChange={(isOpen) =>
                setOpenViewportDropdown(isOpen ? "environmentProps" : null)
              }
            />
          </>
        )}
      </ViewportTools>
      <AnimatePresence initial={false}>
        {isSwap &&
          currentTab === SelectedTab.Timeline && (
            <>
              <motion.div
                key="sequence-picker-toolbar"
                initial={{ opacity: 0, y: -10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="absolute left-[80px] top-[16px] z-[1000]"
              >
                <button
                  type="button"
                  className="flex items-center gap-[6px] rounded-full border border-creator-border bg-creator-surface px-[12px] py-[8px] text-[12px] font-medium text-creator-text shadow-md transition-colors hover:bg-creator-primary-background"
                  onClick={() => dispatch(closeSequencePositionPicker())}
                >
                  <ArrowBackIcon className="text-[16px]" fontSize="inherit" />
                  Back to take
                </button>
              </motion.div>
            </>
          )}
      </AnimatePresence>
      {currentTab === SelectedTab.Timeline && (
        <>
          <PropLibrary
            isOpen={isPropLibraryOpen}
            onClose={closePropLibrary}
            title={"Add New Prop"}
          />
          <QuickActorModal
            isOpen={isQuickActorModalOpen}
            onClose={closeQuickActorModal}
            title={"Add New Actor"}
            width={"calc(90vw / var(--app-zoom, 1))"}
            height={"calc(90vh / var(--app-zoom, 1))"}
          />
        </>
      )}
    </div>
  );
}
